// Script-with-callouts surface — Tate's #6, day-0.1 rev 3 (Ryan feedback).
//
// Updates rev 3 (2026-06-07 PM):
//   - The parsed_script has 13 "scenes" because every PAGE of the OCR was
//     labeled as its own scene ("Act I — The Prozorov Sitting Room", "Act
//     I — Irina's name day", "Act I — The Prozorov House"…). The actor sees
//     the same "What's your scene objective here?" five times in Act One.
//     Fix: detect the real ACT from the scene name (Act I / Act II / etc.)
//     and dedupe — one objective prompt per actual Act, not per page.
//     Page-scenes inside an Act render as continuous text under one Act
//     header, with mid-act varied prompts as before.
// Updates rev 2:
//   - Varied margin prompts: scene-start = objective (Cael-locked); mid-
//     scene every ~12 cues, rotate through substitution / beat /
//     given_circumstances / note.
//   - Tap opens journal on phone (onPointerUp, stopPropagation, manipulation).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DefineResult, getMethodologyPrompts, MethodologyPrompt,
  postAnalyze, postDefine, postResearchKeywords, ResearchKeyword,
} from "../api/client";
import { useArc } from "../state/arc";
import JournalOverlay from "./JournalOverlay";

interface MarginPrompt {
  anchorLineId: number;
  kind:         string;
  label:        string;
  text:         string;
  toolNumber:   number | null;
  private:      boolean;
  locked:       boolean;
  sequence:     number;
}

// Extract the Act roman/arabic from a scene name string ("ACT ONE", "Act I",
// "Act II — Lunch", "Act Two", "Scene 3" → null, etc.). The aggregator
// produced one parsed "scene" per page, so Act I spans ~5 page-scenes; we
// need to fold them back into one logical Act for prompt placement +
// header rendering.
function actNumberFromName(name: string): number | null {
  const s = (name || "").toUpperCase();
  // "ACT IV" / "ACT III" / "ACT II" / "ACT I"
  const roman = s.match(/\bACT\s+(I{1,3}V?|IV|V|VI{1,3}|VII?)\b/);
  if (roman) {
    const r = roman[1];
    const map: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7 };
    if (map[r] !== undefined) return map[r];
  }
  // "ACT ONE" / "ACT TWO"
  const word = s.match(/\bACT\s+(ONE|TWO|THREE|FOUR|FIVE)\b/);
  if (word) {
    const map: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
    return map[word[1]] ?? null;
  }
  // "ACT 1" / "ACT 2"
  const num = s.match(/\bACT\s+(\d+)/);
  if (num) return parseInt(num[1], 10);
  return null;
}

function actLabel(n: number): string {
  return (["ACT ONE", "ACT TWO", "ACT THREE", "ACT FOUR", "ACT FIVE", "ACT SIX", "ACT SEVEN"][n - 1]) || `ACT ${n}`;
}

export default function ScriptWithCallouts() {
  const navigate = useNavigate();
  const { state, update } = useArc();
  const script = state.parsedScript;
  const project = state.parsedProject;
  const character = state.selectedCharacter;

  const [defineResult, setDefineResult] = useState<DefineResult | null>(null);
  const [defineLoading, setDefineLoading] = useState(false);
  const [journalLineId, setJournalLineId] = useState<number | null>(null);
  const [journalKind, setJournalKind] = useState<string>("objective");
  const [journalPrompt, setJournalPrompt] = useState<MethodologyPrompt | null>(null);
  const [keywords, setKeywords] = useState<ResearchKeyword[]>([]);
  const [breakdownPrompts, setBreakdownPrompts] = useState<MethodologyPrompt[]>([]);
  const [showBeats, setShowBeats] = useState(false);
  const [analyzingBeats, setAnalyzingBeats] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // ----- Group page-scenes by their ACT (folding "Act I — XXX" page-scenes
  // back into one logical Act) ---------------------------------------------
  const actGroups = useMemo(() => {
    type Group = { actNum: number | null; headerName: string; sceneIds: number[]; lineIds: number[] };
    const groups: Group[] = [];
    if (!script) return groups;
    let lastAct: number | null = -1;
    for (const sc of script.scenes) {
      const act = actNumberFromName(sc.name);
      if (act !== null && act !== lastAct) {
        // New act starts
        groups.push({
          actNum: act,
          headerName: actLabel(act),
          sceneIds: [sc.order_index],
          lineIds: [...sc.line_ids],
        });
        lastAct = act;
      } else if (act !== null && act === lastAct) {
        // Continuation of the same act — fold into the previous group
        const g = groups[groups.length - 1];
        g.sceneIds.push(sc.order_index);
        g.lineIds.push(...sc.line_ids);
      } else {
        // No detectable Act in the name (rare — leave it as its own group)
        groups.push({
          actNum: null,
          headerName: sc.name,
          sceneIds: [sc.order_index],
          lineIds: [...sc.line_ids],
        });
        lastAct = -1;
      }
    }
    return groups;
  }, [script]);

  // ----- Fetch methodology-aware Scene Breakdown prompt sequence -----
  // Per the spec at /My Drive/acting-craft-mind-map-research-2026-06-01/
  // 04-actor-journal-and-script-markup-spec.md: each methodology has its OWN
  // ordered prompt list for Scene Breakdown — Chubbuck = 11 prompts in cart-
  // before-the-horse order; Meisner = 1; PA = 4 in order; Hagen = 10. We
  // walk that list IN ORDER across the actor's lines, one prompt per anchor.
  useEffect(() => {
    let cancelled = false;
    const m = state.methodologyId || "stanislavski";
    getMethodologyPrompts(m, "scene_breakdown")
      .then((res) => { if (!cancelled) setBreakdownPrompts(res.prompts); })
      .catch(() => { /* silent; fall back to nothing */ });
    return () => { cancelled = true; };
  }, [state.methodologyId]);

  // ----- Place prompts at YOUR lines, IN ORDER per methodology sequence -----
  // Ryan: "the margin prompts are asking me questions about scenes I'm not
  // even in. It's really phoning it in." Anchor every prompt to YOUR lines.
  // The sequence is methodology-driven now — one tool per prompt slot, no
  // duplication. Chubbuck walks #2 → #3 → #4 → … through your lines.
  const promptsByAnchorLine = useMemo(() => {
    const m = new Map<number, MarginPrompt[]>();
    if (!script || !character || breakdownPrompts.length === 0) return m;

    // Build the flat sequence of your lines across all Acts where you appear.
    const yourLines: typeof script.lines = [];
    for (const g of actGroups) {
      const inAct = script.lines.filter(
        (ln) => g.lineIds.includes(ln.id) && ln.character === character,
      );
      yourLines.push(...inAct);
    }
    if (yourLines.length === 0) return m;

    // How many of your lines between consecutive prompts? Spread the
    // sequence across the play so the actor isn't hit with 11 questions in
    // the first 11 lines. Floor at one prompt every 4 of your lines.
    const stride = Math.max(4, Math.floor(yourLines.length / breakdownPrompts.length));

    for (let pi = 0; pi < breakdownPrompts.length; pi++) {
      const lineIdx = Math.min(pi * stride, yourLines.length - 1);
      const ln = yourLines[lineIdx];
      const p = breakdownPrompts[pi];
      const list = m.get(ln.id) || [];
      list.push({
        anchorLineId: ln.id,
        kind:         p.kind,
        label:        p.label,
        text:         p.text,
        toolNumber:   p.tool_number,
        private:      p.private,
        locked:       p.locked,
        sequence:     p.sequence,
      });
      m.set(ln.id, list);
    }

    return m;
  }, [script, character, actGroups, breakdownPrompts]);

  // ----- Pull research keywords once -----
  useEffect(() => {
    let cancelled = false;
    if (!project) return;
    postResearchKeywords(project, character ?? undefined)
      .then((res) => { if (!cancelled) setKeywords(res.keywords); })
      .catch(() => { /* keywords are best-effort */ });
    return () => { cancelled = true; };
  }, [project, character]);

  // ----- highlight-to-define -----
  function onSelectionEnd() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const word = sel.toString().trim().split(/\s+/)[0];
    if (!word || word.length < 2 || word.length > 40) return;
    let context = "";
    const anchor = sel.anchorNode?.parentElement?.closest("[data-cue-text]");
    if (anchor) context = anchor.textContent || "";
    setDefineLoading(true);
    setDefineResult({ word, definition: "Looking it up…", kind: "common", register_note: null });
    postDefine(word, context.slice(0, 300), project?.script_name)
      .then((res) => setDefineResult(res))
      .catch(() => setDefineResult({
        word, definition: "Couldn't reach the definer.",
        kind: "common", register_note: null,
      }))
      .finally(() => setDefineLoading(false));
  }

  function closeDefine() {
    setDefineResult(null);
    window.getSelection()?.removeAllRanges();
  }

  function openJournal(lineId: number, kind: string, prompt: MethodologyPrompt | null = null) {
    setJournalLineId(lineId);
    setJournalKind(kind);
    setJournalPrompt(prompt);
  }

  function openJournalFromTab() {
    // Slide-tab opens the journal with no specific anchor — actor's open notebook.
    setJournalLineId(0);
    setJournalKind("note");
    setJournalPrompt(null);
  }

  // ----- Beats — Chubbuck Tool #6 markup grammar ---------------------------
  // Map each dialogue line.id to its beat (if analysis loaded). The
  // analyzer's beat.cue_start / cue_end are 0-based indexes within the
  // scene's dialogue cues — we walk each scene's dialogue lines in order
  // to translate those into the rich-line ids the script renders.
  interface BeatInfo {
    beatId: string;
    name: string;
    intentionShift: string;
    isFirstLine: boolean;
    isLastLine: boolean;
  }

  const beatByLineId = useMemo(() => {
    const m = new Map<number, BeatInfo>();
    if (!script || !state.analysis) return m;
    for (const sceneAnalysis of state.analysis.scene_analyses || []) {
      const scene = script.scenes.find((s) => s.order_index === sceneAnalysis.scene_idx);
      if (!scene) continue;
      // Dialogue-only lines in this scene, in render order.
      const dlgLines = script.lines.filter(
        (ln) => scene.line_ids.includes(ln.id) && ln.character,
      );
      for (const beat of sceneAnalysis.beats || []) {
        const start = Math.max(0, beat.cue_start);
        const end   = Math.min(dlgLines.length - 1, beat.cue_end);
        for (let i = start; i <= end; i++) {
          const ln = dlgLines[i];
          if (!ln) continue;
          m.set(ln.id, {
            beatId: beat.beat_id,
            name:   beat.name,
            intentionShift: beat.intention_shift,
            isFirstLine: i === start,
            isLastLine:  i === end,
          });
        }
      }
    }
    return m;
  }, [script, state.analysis]);

  async function fireAnalyzer() {
    if (!project || !character || !state.methodologyId) {
      setAnalyzeError("Need character + methodology first.");
      return;
    }
    setAnalyzingBeats(true);
    setAnalyzeError(null);
    try {
      const analysis = await postAnalyze({
        project,
        methodology_id: state.methodologyId,
        selected_characters: [character],
      });
      update({ analysis });
    } catch (e: any) {
      setAnalyzeError(e?.message ?? "Analyzer failed.");
    } finally {
      setAnalyzingBeats(false);
    }
  }

  // ----- jump-to-entrance — actor's first dialogue line --------------------
  const entranceLineId = useMemo(() => {
    if (!script || !character) return null;
    const first = script.lines.find((ln) => ln.character === character);
    return first?.id ?? null;
  }, [script, character]);

  function jumpToEntrance() {
    if (entranceLineId == null) return;
    const el = document.querySelector(`[data-line-id="${entranceLineId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief gold flash for orientation
      const target = el as HTMLElement;
      target.style.transition = "background 600ms ease";
      target.style.background = "rgba(183,154,90,0.12)";
      window.setTimeout(() => { target.style.background = "transparent"; }, 1400);
    }
  }

  if (!script) {
    return (
      <div className="v2">
        <h2>No script loaded.</h2>
        <button className="v2-affordance" onClick={() => navigate("/upload")}>← Upload</button>
      </div>
    );
  }

  return (
    <div className="v2" onMouseUp={onSelectionEnd} onTouchEnd={onSelectionEnd}>
      <p className="v2-tracked-caps" style={{ textAlign: "center" }}>{script.title}</p>
      <h2 style={{ textAlign: "center", marginTop: 0 }}>
        {character ? `${character}'s script` : "The script"}
      </h2>
      <p className="v2-prompt" style={{ textAlign: "center" }}>
        Tap a margin question to answer in your journal. Select any word for a definition.
      </p>

      {entranceLineId !== null && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "0.6rem" }}>
          <button className="v2-affordance" onClick={jumpToEntrance}>
            Jump to your entrance
          </button>
        </div>
      )}

      {/* Show-beats toggle. If no analysis yet, the toggle kicks one off. */}
      <div className="v2-script-toggle-row">
        <button
          className={"v2-script-toggle" + (showBeats ? " is-on" : "")}
          onPointerUp={() => {
            if (state.analysis) {
              setShowBeats((v) => !v);
            } else if (!analyzingBeats) {
              setShowBeats(true);
              fireAnalyzer();
            }
          }}
          disabled={analyzingBeats}
        >
          {analyzingBeats ? "Reading…" : showBeats ? "Beats · on" : "Show beats"}
        </button>
      </div>
      {analyzingBeats && (
        <p className="v2-stage-event-detail" style={{ textAlign: "center", margin: "0 0 1rem" }}>
          The analyzer is reading the whole script. ~10 minutes for a full play.
        </p>
      )}
      {analyzeError && (
        <p className="v2-stage-event-detail" style={{ textAlign: "center", color: "#B83E3E" }}>
          {analyzeError}
        </p>
      )}

      <hr className="v2-hairline" />

      <div className="v2-paper">
        {actGroups.map((g, gi) => {
          const actLines = script.lines.filter((ln) => g.lineIds.includes(ln.id));
          return (
            <section key={gi} style={{ marginBottom: "2.5rem" }}>
              {/* One Act header per actual act — not per page-scene. */}
              <p className="v2-tracked-caps">{g.headerName}</p>

              {actLines.map((ln) => {
                const isYou = ln.character === character;
                const prompts = promptsByAnchorLine.get(ln.id) || [];
                const beat = showBeats ? beatByLineId.get(ln.id) : undefined;

                if (!ln.character) {
                  return (
                    <p key={ln.id} className="v2-stage">{ln.text}</p>
                  );
                }
                return (
                  <div
                    key={ln.id}
                    data-line-id={ln.id}
                    style={{ position: "relative" }}
                    className={beat ? "v2-beat-group" : ""}
                  >
                    {beat?.isFirstLine && (
                      <>
                        <span className="v2-beat-open" aria-hidden>{"{"}</span>
                        <div
                          className="v2-beat-meta"
                          onPointerUp={(e) => {
                            e.stopPropagation();
                            openJournal(ln.id, "actions_per_beat", {
                              tool_number: 7,
                              sequence: 0,
                              locked: false,
                              kind: "actions_per_beat",
                              private: false,
                              label: `Action for: ${beat.name}`,
                              text: `${beat.intentionShift}\n\nWhat verb do you play in this beat? Try three candidates aloud. Mark the winner.`,
                            });
                          }}
                          role="button"
                        >
                          <span className="v2-beat-name">{beat.name}</span>
                          {beat.intentionShift}
                        </div>
                      </>
                    )}
                    {prompts.map((p, i) => {
                      // MarginPrompt is the local camelCase shape; convert
                      // back to the snake_case MethodologyPrompt the journal
                      // overlay expects.
                      const mp: MethodologyPrompt = {
                        tool_number: p.toolNumber,
                        sequence:    p.sequence,
                        locked:      p.locked,
                        kind:        p.kind,
                        private:     p.private,
                        label:       p.label,
                        text:        p.text,
                      };
                      return (
                        <button
                          key={`prompt-${ln.id}-${i}`}
                          className="v2-margin-marker"
                          onPointerUp={(e) => { e.stopPropagation(); openJournal(p.anchorLineId, p.kind, mp); }}
                          aria-label={`${p.label} — Tool ${p.toolNumber ?? ""}`}
                          title={`${p.label} — ${p.text}`}
                        >
                          {p.toolNumber !== null ? `#${p.toolNumber}` : "·"}
                        </button>
                      );
                    })}
                    <p className="v2-cue-label">
                      {ln.character}{isYou && " · YOU"}
                    </p>
                    <p
                      className="v2-cue-text"
                      data-cue-text
                      style={isYou ? { color: "var(--v2-ink)" } : { color: "var(--v2-ink-soft)" }}
                    >
                      {ln.text}
                    </p>
                    {beat?.isLastLine && (
                      <span className="v2-beat-close" aria-hidden>{"}"}</span>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>

      {keywords.length > 0 && (
        <div className="v2-worth-a-look">
          <p className="v2-worth-a-look-title">Worth a look.</p>
          {keywords.map((kw) => (
            <div key={`${kw.term}-${kw.first_line_idx}`} className="v2-worth-a-look-item">
              <span className="v2-kw-term">{kw.term}</span>
              <span className="v2-kw-kind">{kw.kind.replace("_", " ")}</span>
              <p className="v2-prompt" style={{ margin: "0.3rem 0 0" }}>{kw.first_line_excerpt}</p>
            </div>
          ))}
        </div>
      )}

      <hr className="v2-hairline" />
      <div style={{ display: "flex", justifyContent: "center", gap: "2.5rem", marginTop: "1.5rem" }}>
        <button className="v2-affordance" onClick={() => navigate("/record")}>Run lines</button>
        <button className="v2-affordance" onClick={() => navigate("/character")}>Different character</button>
      </div>

      {defineResult && (
        <div className="v2-define-popover" onClick={closeDefine} role="button">
          <p className="v2-tracked-caps">{defineResult.kind.replace("_", " ")}</p>
          <p style={{ margin: "0.2rem 0", fontFamily: "var(--v2-serif)", fontSize: "1.1rem" }}>
            <strong>{defineResult.word}</strong> — {defineResult.definition}
          </p>
          {defineLoading && <p className="v2-stage-event-detail">…</p>}
        </div>
      )}

      {journalLineId !== null && state.projectId && (
        <JournalOverlay
          projectId={state.projectId}
          lineId={journalLineId}
          kind={journalKind}
          prompt={journalPrompt}
          allPrompts={breakdownPrompts}
          onClose={() => { setJournalLineId(null); setJournalPrompt(null); }}
        />
      )}

      {/* Left-edge slide tab — journal accessible anywhere on the script */}
      {state.projectId && journalLineId === null && (
        <button
          className="v2-journal-tab"
          onPointerUp={openJournalFromTab}
          aria-label="Open journal"
          title="Open journal"
        >
          <span className="v2-journal-tab-glyph">Journal</span>
        </button>
      )}
    </div>
  );
}
